export interface Bonus {
  number: string
  title: string
  tagline: string
  description: string
  bullets: string[]
  closing: string
  image: string
  alt: string
}

export const bonuses: Bonus[] = [
  {
    number: '01',
    title: '52-Week Yoga Teaching Calendar™',
    tagline: 'Your year of teaching inspiration — already organized.',
    description:
      '52 weeks. 52 directions for your teaching. Instead of arriving at another Monday wondering how to keep your classes fresh, use the calendar to create rhythm and variety across your teaching year.',
    bullets: [
      'Weekly teaching inspiration',
      'Class focus ideas',
      'Intentions',
      'Seasonal variety',
      'Reflection prompts',
      'Easy integration with your Vault classes',
    ],
    closing:
      'Choose the week. Choose the corresponding focus. Find a class. Teach.',
    image:
      'https://cdn.magicpatterns.com/uploads/9cQWeVbNGYGp2W9Cg6rZgj/ChatGPT_Image_7_sept_2026,_23_10_53.webp',
    alt: 'Bonus 01 cover: the yoga teaching planning vault book',
  },
  {
    number: '02',
    title: 'The Ultimate Yoga Cueing Library™',
    tagline: 'When you know the pose — but want better words.',
    description:
      'A practical collection of language you can use throughout class, from the opening moments to the final rest.',
    bullets: [
      'Openings',
      'Breath',
      'Pose entry',
      'Alignment-oriented guidance',
      'Transitions',
      'Standing work',
      'Balance',
      'Floor work',
      'Rest',
      'Closing moments',
    ],
    closing:
      'You don’t need to invent every sentence while you’re teaching. Review the cues, choose the ones that fit, then make them your own.',
    image:
      'https://cdn.magicpatterns.com/uploads/22DrEh9tcdqa3bUbu9eudq/ChatGPT_Image_7_sept_2026,_23_11_56.webp',
    alt: 'Bonus 02 cover: the yoga cueing and anatomy reference book',
  },
  {
    number: '03',
    title: 'Beginner Yoga Modification Guide™',
    tagline: 'More options. Less panic.',
    description:
      'A practical reference for the moment you look around the room and realize not everyone needs the same version.',
    bullets: [
      'Beginner-friendly options',
      'Reduced range of movement',
      'Prop variations',
      'Wall support',
      'Chair support',
      'Balance alternatives',
      'Simpler pathways',
      'Mixed-level teaching',
    ],
    closing:
      'Base → Option → Support. You don’t need three separate classes — you need useful pathways inside one class.',
    image:
      'https://cdn.magicpatterns.com/uploads/3XL1AbUwRANhXkC8w4SBRN/ChatGPT_Image_7_sept_2026,_23_13_17.webp',
    alt: 'Bonus 03 cover: the modification and support guide book',
  },
  {
    number: '04',
    title: '30 Emergency Ready-to-Teach Classes™',
    tagline: 'The “I need a class NOW” library.',
    description:
      'No time? Substituting? Forgot your plan? Unexpected group? Low energy? No props? Open this bonus — the classes are already organized by situation.',
    bullets: [
      '15–20 minute sessions',
      '30-minute sessions',
      '45-minute options',
      'Standard classes',
      'Beginners',
      'Mixed levels',
      'Gentle groups',
      'Higher-energy groups',
      'Low-energy groups',
      'Mobility',
      'No props',
      'Small spaces',
      'Online teaching',
      'Substitute situations',
      'Unknown groups',
    ],
    closing:
      'Open. Choose. Teach. This may be the bonus you’re happiest to have on the day you need it.',
    image:
      'https://cdn.magicpatterns.com/uploads/gr3Dbd531cXyv8kFFcKLRy/ChatGPT_Image_7_sept_2026,_23_15_26.webp',
    alt: 'Bonus 04 cover: the emergency ready-to-teach classes book',
  },
  {
    number: '05',
    title: 'The 60-Second Class Finder™',
    tagline: 'Find the right class without searching the entire Vault.',
    description:
      'The Vault is big, so we created the shortcut. Search by what you actually know about tomorrow’s class.',
    bullets: [
      'Duration',
      'Student level',
      'Style',
      'Intensity',
      'Body focus',
      'Teaching focus',
      'Energy',
      'Props',
      'Room',
      'Teaching situation',
    ],
    closing:
      'Time → Level → Style → Focus → Situation, then Volume → Class → Teach. The goal isn’t more choices — it’s the right choice, faster.',
    image:
      'https://cdn.magicpatterns.com/uploads/gBUtFgHwxZrDvbBT3cCGzc/ChatGPT_Image_7_sept_2026,_23_16_38.webp',
    alt: 'Bonus 05 cover: the sequencing and class finder book',
  },
  {
    number: '06',
    title: 'New Yoga Teacher Confidence Kit™',
    tagline:
      'Everything you wish someone had told you before your first classes.',
    description:
      'You forget the next pose. Students look confused. Someone arrives late. You finish early. This practical guide gives you simple ways to navigate the common teaching moments.',
    bullets: [
      'Pre-class checklist',
      'Opening scripts',
      'Sequence memory system',
      'Teaching checkpoints',
      'Mistake recovery',
      'Mixed-level strategies',
      'Timing rescue',
      'Confidence language',
      'Post-class reset',
      'First 10 Classes Roadmap',
    ],
    closing:
      'Confidence isn’t knowing that nothing will go wrong. It’s knowing what to do next.',
    image:
      'https://cdn.magicpatterns.com/uploads/rfFGpm3wkMrYPpmDmavGgf/ChatGPT_Image_7_sept_2026,_23_17_54.webp',
    alt: 'Bonus 06 cover: the class themes and confidence book',
  },
  {
    number: '07',
    title: 'Yoga Teacher Planner & Reflection Journal™',
    tagline: 'Plan. Teach. Reflect. Reuse.',
    description:
      'Turn everything inside your Vault into a repeatable teaching system, with practical pages for the whole cycle of a teaching month.',
    bullets: [
      'Monthly & weekly planning',
      'Individual classes',
      'Class architecture',
      'Sequences',
      'Timing',
      'Openings',
      'Cueing notes',
      'Transitions',
      'Modifications',
      'Props',
      'Student observations',
      'Post-class reflection',
      'Class rotation',
      'Favorite classes',
      'Theme tracking',
      'Backup classes',
      'Monthly reviews',
      'Confidence tracking',
    ],
    closing:
      'Plan → Teach → Reflect → Reuse. The class you planned today shouldn’t disappear after you teach it — it should make your next class easier.',
    image:
      'https://cdn.magicpatterns.com/uploads/1xF7iJiDSUQ4skbdma49Mw/ChatGPT_Image_7_sept_2026,_23_19_00.webp',
    alt: 'Bonus 07 cover: the teacher planner and practice vault book',
  },
]
